<template>
  <section
    data-cy="contact-details-view"
    :data-view-ready="!loading && !!contact">
    <div>
      <coozzy-page-title
        :title="$tc('message.navigation.contact.title')"
        :action-text="isEditor ? $tc('message.contact.newContact') : ''"
        @click="createNewContactClicked()" />
      <div
        id="contactSuggestion"
        class="row">
        <div
          class="col-12 col-md-5 mb-2">
          <contact-search-provider
            :filters="contactSearchFilters"
            silent>
            <template #default="{ hits, refine }">
              <vue-autosuggest
                :key="refreshAutosuggestKey"
                v-model="emailSuggestion"
                :suggestions="hitsToSuggestions(hits)"
                :input-props="inputProps"
                @input="onChange(refine,emailSuggestion)"
                @selected="onSelect">
                <template #default="{ suggestion }">
                  <strong>{{ getSuggestionContactName(suggestion) }}</strong>
                  <br>
                  <address-text
                    :show-short-country="true"
                    :hide-if-empty="true"
                    :address="getSuggestionAddress(suggestion)" />
                  <template v-if="!isOwnerModule">
                    <br>
                    {{ getSuggestionOwnerName(suggestion) }}
                  </template>
                </template>
              </vue-autosuggest>
            </template>
          </contact-search-provider>
        </div>
      </div>
      <div
        v-if="contact"
        class="row mt-2">
        <div class="col-12 col-md-5">
          <div class="row">
            <div class="col-12 col-md-12">
              <coozzy-detail-card
                data-cy="contact-details-main-card"
                :title="!contact.numericId ? $tc('message.contact.title') : $tc('message.contact.title') + ' ' + contact.numericId + ' - ' + contactOwnerName"
                :link-activity="true"
                :is-editable="!(hasContactType(contact, 'CLIENT') || !isEditor)"
                @onEdit="editContactClicked(contact)"
                @goToActivities="openActivities()"
                @goToActivitiesMiddle="openActivitiesMiddle()"
                @onAccept="onApplicationFormAllInfoAccepted()">
                <div
                  v-if="loading"
                  class="p-0 text-center">
                  <coozzy-spinner class="m-3" />
                </div>
                <contact-detail-card
                  v-else
                  :ref="'contact-detail-card'"
                  :key="uniqueId"
                  :contact="contact"
                  :type="'self'"
                  @edit-address="editAddress"
                  @contact-edited="contactEdited" />
              </coozzy-detail-card>
            </div>
            <div
              v-if="countRelationsContacts !== 0"
              class="col-12 col-md-12">
              <coozzy-detail-card
                v-for="item in relationsContacts"
                :key="item.id"
                :title="$t('message.contact.title') + ' ' + item.numericId + ' - ' + getContactOwnerName(item.ownerId)"
                :is-editable="!(hasContactType(item, 'CLIENT') || !isEditor)"
                :is-linked="!(hasContactType(item, 'CLIENT') || !isEditor)"
                @onEdit="editContactClicked(item)"
                @onUnlink="openUnlinkModal(item)">
                <div
                  v-if="loading"
                  class="p-0 text-center">
                  <coozzy-spinner class="m-3" />
                </div>
                <contact-detail-card
                  v-else
                  :ref="'contact-detail-card'"
                  :key="uniqueId"
                  :contact="item"
                  @edit-address="editAddress"
                  @contact-edited="contactEdited" />
              </coozzy-detail-card>
            </div>
            <div
              v-if="countContactPerson !== 0 && typeOfContact === 'company' && isEditor"
              class="col-12 col-md-12">
              <coozzy-detail-card
                data-cy="contact-persons-card"
                :title="$t('message.tableColumnHeadings.contactPersons') + ' (' + countContactPerson +')'">
                <div class="col p-0">
                  <b-table
                    ref="contactPersonTable"
                    bordered
                    hover
                    responsive="true"
                    stacked="md"
                    class="m-0"
                    select-mode="single"
                    :busy="loading"
                    :fields="fieldsContactPersons"
                    :items="countContactPerson <= 5 || showAllContacts ? contactPerson : contactPerson.slice(0,5)">
                    <!-- Busy state -->
                    <template #table-busy>
                      <div class="text-center text-danger my-2">
                        <coozzy-spinner />
                      </div>
                    </template>

                    <!-- Headings -->
                    <template #head(name)>
                      {{ $t('message.generic.name') }}
                    </template>
                    <template #head(phoneNumber)>
                      {{ $t('message.generic.phoneNumber') }}
                    </template>
                    <template #head(email)>
                      {{ $t('message.generic.email') }}
                    </template>
                    <template #head(action)>
                      {{ '' }}
                    </template>

                    <!-- Columns -->
                    <template
                      #cell(name)="data">
                      <div
                        class="clickable-cell cursor-pointer"
                        data-cy="contact-person-row-name"
                        :class="[isDeath(data.item.dateOfDeath) || !data.item.active ? 'text-gray' : '']"
                        @click="showPerson(data.item)">
                        <span>
                          <template v-if="data.item.gender === 'MALE'">
                            {{ $t('message.generic.title.mr') }}
                          </template>
                          <template v-if="data.item.gender === 'FEMALE'">
                            {{ $t('message.generic.title.mrs') }}
                          </template>
                          <template v-if="data.item.gender === 'OTHER'">
                            {{ $t('message.generic.title.divers') }}
                          </template>
                          <template v-if="data.item.gender === 'HEIRS'">
                            {{ $t('message.generic.title.heirs') }}
                          </template>
                        </span>
                        <span
                          v-if="data.item.title && data.item.title !== ''">{{ data.item.title }} </span>
                        <div class="font-weight-bold">
                          {{ data.item.name | displayOptionalValue }}
                        </div>
                        <div v-if="data.item.employment && data.item.employment.companyPosition !== ''">
                          {{ data.item.employment.companyPosition }}
                        </div>
                        <small
                          v-if="data.item.preferredLanguage && data.item.preferredLanguage !== 'PREFERRED_LANGUAGE_UNDEFINED'">{{
                            $t('message.contact.preferredLanguage')
                          }}: {{ $t('message.contact.preferredLanguageValue.' + data.item.preferredLanguage) }}</small>
                      </div>
                    </template>
                    <template
                      #cell(phoneNumber)="data">
                      <div
                        v-if="getPhoneNumbers(data.item.phoneNumbers, 'WORK') !== '-'"
                        class="row"
                        :class="[isDeath(data.item.dateOfDeath) || !data.item.active ? 'text-gray' : '']">
                        <div class="col-1">
                          <coozzy-phone-icon />
                        </div>
                        <div class="col">
                          <a
                            class="link"
                            :href="'tel:' + getPhoneNumbers(data.item.phoneNumbers, 'WORK')">
                            {{ getPhoneNumbers(data.item.phoneNumbers, 'WORK') }}
                          </a>
                        </div>
                      </div>
                      <div
                        v-if="getPhoneNumbers(data.item.phoneNumbers, 'MOBILE') !== '-'"
                        class="row"
                        :class="[isDeath(data.item.dateOfDeath) || !data.item.active ? 'text-gray' : '']">
                        <div class="col-1">
                          <coozzy-mobile-alt-icon />
                        </div>
                        <div class="col">
                          <a
                            class="link"
                            :href="'tel:' + getPhoneNumbers(data.item.phoneNumbers, 'MOBILE')">
                            {{ getPhoneNumbers(data.item.phoneNumbers, 'MOBILE') }}
                          </a>
                        </div>
                      </div>
                    </template>
                    <template
                      #cell(email)="data">
                      <div
                        class="row"
                        :class="[isDeath(data.item.dateOfDeath) || !data.item.active ? 'text-gray' : '']">
                        <div class="col-1">
                          <img
                            class="email-icon-img"
                            src="@/assets/icon/arroba.svg"
                            alt="Placeholder">
                        </div>
                        <div class="col">
                          <template v-if="data.item.email !== ''">
                            <a
                              class="link"
                              @click="sendEmailModal(data.item)">{{ data.item.email }}</a>
                          </template>
                          <template v-else>
                            {{ data.item.email | displayOptionalValue }}
                          </template>
                        </div>
                      </div>
                    </template>
                    <template
                      #cell(action)="data">
                      <coozzy-button
                        class="col-1 p-0"
                        data-cy="contact-person-row-edit-button"
                        design="transparent"
                        @click="showPerson(data.item)">
                        <coozzy-edit-icon />
                      </coozzy-button>
                    </template>
                  </b-table>
                  <div class="text-center">
                    <a
                      v-if="countContactPerson > 5"
                      class="link"
                      @click="showAllContacts = !showAllContacts">
                      <coozzy-angle-double-up-icon v-if="showAllContacts === true" />
                      <coozzy-angle-double-down-icon v-else />
                    </a>
                  </div>
                </div>
              </coozzy-detail-card>
            </div>
            <div
              v-if="countDocument !== 0 && !isJanitor"
              class="col-12 col-md-12">
              <coozzy-detail-card
                data-cy="contact-documents-card"
                :title="$t('message.generic.documents') + ' (' + countDocument +')'"
                :download-all="true"
                @download-all-documents="downloadContactDocuments()">
                <div class="col p-0">
                  <document
                    v-for="doc in (countDocument <= 5 || showAllDocuments ? listDocuments : listDocuments.slice(0,5))"
                    :key="doc.id"
                    :document="doc"
                    class="no-lower-height"
                    :owner="getDocumentOwner(doc.id)"
                    :show-in-portal-checkbox="false"
                    @version-added="versionAdded"
                    @delete-document="deleteDocument" />
                  <div class="text-center">
                    <a
                      v-if="countDocument > 5"
                      class="link col"
                      @click="showAllDocuments = !showAllDocuments">
                      <coozzy-angle-double-up-icon v-if="showAllDocuments === true" />
                      <coozzy-angle-double-down-icon v-else />
                    </a>
                  </div>
                </div>
              </coozzy-detail-card>
            </div>
            <div
              v-if="listRentalDocuments.length !== 0 && !isJanitor"
              class="col-12 col-md-12">
              <coozzy-detail-card
                :title="$t('message.generic.rentalDocuments') + ' (' + listRentalDocuments.length +')'"
                :download-all="true"
                @download-all-documents="downloadRentalDocuments($event)">
                <div class="col p-0">
                  <document
                    v-for="doc in (listRentalDocuments.length <= 5 || showAllRentalDocuments ? listRentalDocuments : listRentalDocuments.slice(0,5))"
                    :key="doc.id"
                    :document="doc"
                    :use-label="true"
                    class="no-lower-height"
                    @version-added="rentalDocumentUpdated"
                    @delete-document="deleteRentalDocument" />
                  <div class="text-center">
                    <a
                      v-if="listRentalDocuments.length > 5"
                      class="col"
                      @click="showAllRentalDocuments = !showAllRentalDocuments">
                      <coozzy-angle-double-up-icon v-if="showAllRentalDocuments === true" />
                      <coozzy-angle-double-down-icon v-else />
                    </a>
                  </div>
                </div>
              </coozzy-detail-card>
            </div>
            <div
              v-if="listCondominiumDocuments.length !== 0 && !isJanitor"
              class="col-12 col-md-12">
              <coozzy-detail-card
                :title="$t('message.generic.condominiumDocuments') + ' (' + listCondominiumDocuments.length +')'"
                :download-all="true"
                @download-all-documents="downloadCondominiumDocuments()">
                <div class="col p-0">
                  <document
                    v-for="doc in (listCondominiumDocuments.length <= 5 || showCondominiumDocuments ? listCondominiumDocuments : listCondominiumDocuments.slice(0,5))"
                    :key="doc.id"
                    :document="doc"
                    :use-label="true"
                    class="no-lower-height"
                    @version-added="condominiumDocumentUpdated"
                    @delete-document="deleteCondominiumDocuments" />
                  <div class="text-center">
                    <a
                      v-if="listCondominiumDocuments.length > 5"
                      class="col"
                      @click="showCondominiumDocuments = !showCondominiumDocuments">
                      <coozzy-angle-double-up-icon v-if="showCondominiumDocuments === true" />
                      <coozzy-angle-double-down-icon v-else />
                    </a>
                  </div>
                </div>
              </coozzy-detail-card>
            </div>
          </div>
        </div>
        <div
          class="col-12 col-md-7">
          <div class="row">
            <div
              v-if="isEditor"
              class="col-12 col-md-12">
              <coozzy-detail-card
                :title="$tc('message.contact.actions.title')">
                <div class="col-12 col-md-12 mt-3 d-flex flex-wrap">
                  <coozzy-button
                    data-cy="contact-send-email-button"
                    class="mr-3 mb-3"
                    :disabled="!contact.active"
                    @click="openSendMailModal()">
                    <img
                      class="email-icon-img mb-1"
                      src="@/assets/icon/arroba.svg"
                      alt="Placeholder">
                    <span class="ml-1">{{ $t('message.contact.actions.email') }} </span>
                    <coozzy-favorite-icon
                      v-if="contact.preferredCommunicationChannel=== 'PREFERRED_COMMUNICATION_CHANNEL_EMAIL'"
                      :statefavorite="true" />
                  </coozzy-button>

                  <coozzy-button
                    data-cy="contact-send-letter-button"
                    class="mr-3 mb-3"
                    :disabled="!contact.active"
                    @click="openSerialEmail()">
                    <img
                      class="letter-img mb-1"
                      src="@/assets/icon/emailLetter.svg"
                      alt="Placeholder">
                    <span class="ml-1">{{ $t('message.contract.letter') }} </span>
                    <coozzy-favorite-icon
                      v-if="contact.preferredCommunicationChannel=== 'PREFERRED_COMMUNICATION_CHANNEL_LETTER'"
                      :statefavorite="true" />
                  </coozzy-button>

                  <coozzy-button
                    data-cy="contact-create-reminder-button"
                    class="mr-3 mb-3"
                    :disabled="!contact.active"
                    @click="createReminderClicked(null)">
                    <coozzy-reminder-icon class="mb-1" />
                    <span class="ml-1">{{ $t('message.contact.actions.reminder') }} </span>
                  </coozzy-button>

                  <coozzy-button
                    data-cy="contact-create-activity-button"
                    class="mr-3 mb-3"
                    :disabled="!contact.active"
                    @click="editActivity(null)">
                    <coozzy-activity-icon class="mb-1" />
                    <span class="ml-1">{{ $t('message.contact.actions.activity') }} </span>
                  </coozzy-button>

                  <coozzy-button
                    data-cy="contact-create-event-button"
                    class="mr-3 mb-3"
                    :disabled="!contact.active"
                    @click="createEventClicked(null)">
                    <coozzy-calendar-check-icon class="mb-1" />
                    <span class="ml-1">{{ $t('message.contact.actions.event') }} </span>
                  </coozzy-button>

                  <coozzy-button
                    v-if="!isExternalEmployee"
                    data-cy="contact-add-document-button"
                    class="mr-3 mb-3"
                    :disabled="!contact.active"
                    @click="addDocumentClicked('contactDocument')">
                    <coozzy-document-icon class="mb-1" />
                    <span class="ml-1">{{ $t('message.contact.actions.document') }} </span>
                  </coozzy-button>
                  <coozzy-button
                    v-if="!isExternalEmployee && typeOfContact === 'company'"
                    data-cy="contact-add-contact-person-button"
                    class="mr-3 mb-3"
                    :disabled="!contact.active"
                    @click="openContactModal()">
                    <coozzy-contact-person-icon class="mb-1" />
                    <span class="pb-1 ml-1">{{ $t('message.contact.actions.contactPerson') }} </span>
                  </coozzy-button>

                  <coozzy-button
                    v-if="!isExternalEmployee"
                    data-cy="contact-link-contact-button"
                    class="mr-3 mb-3"
                    :disabled="!contact.active"
                    @click="linkContact()">
                    <coozzy-contact-person-icon class="mb-1" />
                    <span class="ml-1">{{ $t('message.contact.actions.linkContact') }} </span>
                  </coozzy-button>

                  <coozzy-button
                    v-if="!isExternalEmployee"
                    data-cy="contact-add-address-button"
                    class="mr-3 mb-3"
                    :disabled="!contact.active"
                    @click="addAddress()">
                    <coozzy-address-card-icon class="mb-1" />
                    <span class="ml-1">{{ $t('message.contact.actions.addAddress') }} </span>
                  </coozzy-button>
                  <coozzy-button
                    v-if="isTicketingEnabled && (isEditor || isTicketEditor)"
                    data-cy="contact-create-ticket-button"
                    class="mr-3 mb-3"
                    :disabled="!contact.active"
                    @click="createTicket()">
                    <coozzy-ticket-icon
                      :name="'ticket'"
                      class="mb-1" />
                    <span class="ml-1">{{ $t('message.contact.actions.createTicket') }} </span>
                  </coozzy-button>
                  <coozzy-button
                    v-if="isBriefButlerEnabled"
                    class="mr-3 mb-3"
                    :disabled="!contact.active"
                    @click="openBriefButlerModal()">
                    <coozzy-brief-butler-icon
                      :name="'briefButler'"
                      class="mb-1 m-0" />
                    <span class="ml-1">{{ $t('message.contact.actions.briefButler') }} </span>
                  </coozzy-button>
                </div>
              </coozzy-detail-card>
            </div>
            <div
              class="col-12 col-md-12">
              <tenancies-contact-card
                v-if="(hasContactType(contact, 'TENANT') || hasContactType(contact, 'SUB_TENANT')) && !isJanitor"
                :ref="'tenancies-contact-card'"
                :key="uniqueId"
                :available-contact="availableContact"
                :contact="contact"
                @list-tenancies="loadRentalDocument" />
            </div>
            <div
              class="col-12 col-md-12">
              <tenancies-contact-card
                v-if="hasContactType(contact, 'GUARANTOR')"
                :ref="'tenancies-contact-card'"
                :key="uniqueId"
                :type="'GUARANTOR'"
                :available-contact="availableContact"
                :contact="contact"
                @list-tenancies="loadRentalDocument" />
            </div>
            <div
              class="col-12 col-md-12">
              <condominium-contact-card
                v-if="hasContactType(contact, 'CONDOMINIUM_OWNER') && !isJanitor"
                :ref="'condominiums-contact-card'"
                :key="uniqueId"
                :available-contact="availableContact"
                :contact="contact"
                @list-condominium="loadCondominiumDocuments" />
            </div>
            <div
              v-if="isEditor || isJanitor"
              class="col-12 col-md-12">
              <building-list-contact-card
                v-if="(hasContactType(contact, 'CRAFTSMAN') || hasContactType(contact, 'CARETAKER')) || hasContactType(contact, 'SERVICE_PROVIDER') || hasContactType(contact, 'OWNER') || isJanitor"
                :ref="'building-list-contact-card'"
                :key="uniqueId"
                :available-contact="availableContact"
                :contact="contact"
                @list-building="fillBuildings" />
            </div>
            <div
              v-if="countSignRequests !== 0 && (isEditor || isJanitor)"
              class="col-12 col-md-12">
              <coozzy-detail-card
                :title="$t('message.deepSign.title') + ' (' + countSignRequests + ')'">
                <div class="col p-0">
                  <b-table
                    ref="signRequestTable"
                    bordered
                    hover
                    class="m-0"
                    selectable
                    :busy="isBusySignature"
                    :fields="fieldsSignature"
                    :items="countSignRequests <= 5 || showAllSignatures ? signatureRequests : signatureRequests.slice(0,5)"
                    @row-hovered="mouseEnterEvents($event)"
                    @row-unhovered="mouseLeaveEvents($event)"
                    @row-clicked="onRowClicked">
                    <!-- Busy state -->
                    <template #table-busy>
                      <div class="text-center text-danger my-2">
                        <coozzy-spinner />
                      </div>
                    </template>

                    <!-- Headings -->
                    <template #head(created)>
                      {{ $t('message.deepSign.created') }}
                    </template>
                    <template #head(contacts)>
                      {{ $t('message.deepSign.contacts') }}
                    </template>
                    <template #head(documentType)>
                      {{ $t('message.deepSign.documentType') }}
                    </template>
                    <template #head(signers)>
                      {{ $t('message.deepSign.signers') }}
                    </template>
                    <template #head(status)>
                      {{ $t('message.deepSign.status.title') }}
                    </template>

                    <!-- Data cells -->
                    <template
                      #cell(created)="data">
                      {{ data.item.created / 1000 | timestampToDate }}
                    </template>
                    <!-- name column -->
                    <template
                      #cell(contacts)="data">
                      <template v-for="item in data.item.signers">
                        <p
                          :key="item.contactId"
                          class="mb-0">
                          <a
                            v-if="contact.id !== item.contactId"
                            class="link contactName"
                            @click="redirectToDetailPage(item.contactId)"
                            @click.middle="redirectToDetailPage(item.contactId, true)"
                            @mousedown.middle.prevent.stop>
                            {{ getSignerName(item) }}
                          </a>
                          <span v-else>
                            {{ getSignerName(item) }}
                          </span>
                        </p>
                      </template>
                    </template>
                    <template
                      #cell(documentType)="data">
                      {{
                        data.item.documentType ? $t('message.manageDocuments.type.' + data.item.documentType.toLowerCase()) : '-'
                      }}
                    </template>
                    <!-- signers name  -->
                    <template
                      #cell(signers)="data">
                      <div
                        id="link-button"
                        class="fieldToShow d-inline"
                        @mousemove="onMouseMove($event)"
                        @mouseleave="showPopover = false">
                        {{ data.item.signers.filter(signer => signer.signed).length }}/{{ data.item.signers.length }}
                      </div>
                    </template>
                    <!-- contactPerson Phone -->
                    <template
                      #cell(status)="data">
                      {{ $t('message.deepSign.status.' + getStatus(data.item)) }}
                    </template>
                  </b-table>
                  <div class="text-center">
                    <a
                      v-if="countSignRequests > 5"
                      class="link"
                      @click="showAllSignatures = !showAllSignatures">
                      <coozzy-angle-double-up-icon v-if="showAllSignatures === true" />
                      <coozzy-angle-double-down-icon v-else />
                    </a>
                  </div>
                  <div
                    v-if="showPopover"
                    id="eventPopover"
                    class="eventPopoverSignRequest"
                    :style="{ left: page.left + 'px', top: page.top + 'px' }">
                    <div
                      v-if="selectedPopOverEvent"
                      class="row">
                      <div class="col employees-calendar">
                        <div class="row">
                          <div
                            v-for="item in selectedPopOverEvent.signers"
                            :key="`${item.contactId}-${item.order}`"
                            class="col-12">
                            {{ getSignerName(item) }}
                            <img
                              v-if="!item.signed"
                              width="15px"
                              height="15px"
                              src="@/assets/icon/hourglass.svg"
                              :alt="'hourglass'">
                            <coozzy-success-icon v-if="item.signed" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </coozzy-detail-card>
            </div>
            <div
              v-if="!isJanitor && contact && contact.id"
              class="col-12 col-md-12">
              <contact-event-card
                :ref="'contact-event-card'"
                :key="uniqueId"
                :contact-id="id"
                :source="'contact'"
                :available-attendees="availableAttendees"
                :contact="contact" />
            </div>
            <div
              v-if="isEditor || isJanitor"
              class="col-12 col-md-12">
              <contact-reminder-card
                :ref="'contact-reminder-card'"
                :key="uniqueId"
                :available-contact="availableContact"
                :contact="contact" />
            </div>
            <div
              v-if="countActivity !== 0 && (isEditor || isJanitor)"
              class="col-12 col-md-12">
              <coozzy-detail-card
                :title="$t('message.ticketOverview.activities.title') + ' (' + countActivity + ')'">
                <div class="col p-0">
                  <b-table
                    ref="activityTable"
                    bordered
                    hover
                    responsive="true"
                    stacked="md"
                    selectable
                    select-mode="single"
                    class="m-0"
                    :busy="isBusyActivity"
                    :fields="fieldsActivity"
                    :items="countActivity <= 5 || showAllActivities ? activityList : activityList.slice(0,5)"
                    @row-clicked="editActivity">
                    <!-- Busy state -->
                    <template #table-busy>
                      <div class="text-center text-danger my-2">
                        <coozzy-spinner />
                      </div>
                    </template>

                    <!-- Headings -->
                    <template #head(date)>
                      {{ $t('message.calendar.reminder.date') }}
                    </template>
                    <template #head(subject)>
                      {{ $t('message.calendar.reminder.subject') }}
                    </template>
                    <template #head(employee)>
                      {{ $t('message.calendar.reminder.employee') }}
                    </template>
                    <template #head(billable)>
                      {{ $t('message.tableColumnHeadings.billable') }}
                    </template>
                    <template #head(hourlyRate)>
                      {{ $t('message.tableColumnHeadings.hourlyRate') }}
                    </template>
                    <template #head(duration)>
                      {{ $t('message.tableColumnHeadings.duration') }}
                    </template>
                    <template #head(amountChf)>
                      {{ $t('message.tableColumnHeadings.amountChf') }}
                    </template>

                    <!-- Columns -->
                    <template
                      #cell(date)="data">
                      {{ data.item.timestampStart | timestampToDateTime }}
                    </template>
                    <template
                      #cell(subject)="data">
                      <p class="m-0 auto-size">
                        <coozzy-paper-clip-icon v-if="data.item.mediaIds?.length > 0" />
                        {{ data.item.subject | displayOptionalValue }}
                      </p>
                    </template>
                    <template
                      #cell(employee)="data">
                      <template
                        v-for="(employeeContactId, index) in data.item.employeeContactIds?.filter(x => getEmployeeById(x) !== '-')">
                        {{ index !== 0 ? ',' : '' }}
                        {{ getEmployeeById(employeeContactId).name | displayOptionalValue }}
                      </template>
                    </template>
                    <template
                      #cell(billable)="data">
                      <div class="d-flex justify-content-center">
                        <coozzy-check-mark-icon v-if="data.item.billable" />
                      </div>
                    </template>
                    <template
                      #cell(hourlyRate)="data">
                      {{ formatHourlyRate(data.item.hourlyRate) }}
                    </template>
                    <template
                      #cell(duration)="data">
                      {{ getDuration(data.item.timestampStart, data.item.timestampEnd) }}
                    </template>
                    <template
                      #cell(amountChf)="data">
                      {{ formatAmountInChf(data.item) }}
                    </template>
                  </b-table>
                  <div class="text-center">
                    <a
                      v-if="countActivity > 5"
                      class="link"
                      @click="showAllActivities = !showAllActivities">
                      <coozzy-angle-double-up-icon v-if="showAllActivities === true" />
                      <coozzy-angle-double-down-icon v-else />
                    </a>
                  </div>
                </div>
              </coozzy-detail-card>
            </div>
            <small-messages-table
              v-if="isEditor && (mailList.length > 0 || filterFolder !== 'FILTER_FOLDER_ALL' || hasEmails)"
              :mail-list="mailList"
              :total-page-mail-count="totalPageMailCount"
              :total-mails-count="totalMailsCount"
              :has-emails="hasEmails"
              :contact="contact"
              @show-email="showEmail"
              @search-emails="searchEmails" />
            <div
              v-if="isTicketingEnabled && (isEditor || isTicketEditor)"
              class="col-12 col-md-12">
              <ticket-contact-card :contact-id="contact.id" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <create-contact-modal
      :key="contactToEdit ? contactToEdit.id : null"
      :for-owner-id="isOwnerModule ? accountId : null"
      :show-search="false"
      :show-request-sources="false"
      :set-interested-party-role="false"
      :show-add-another="true"
      :contact-to-edit="contactToEdit"
      :enable-delete="contactToEdit !== null"
      :list-buildings="lisfOfBuildings"
      @contact-created="contactCreated"
      @contact-updated="contactUpdated"
      @modal-hide="modalHide"
      @contact-deleted="contactDeleted" />
    <create-activity-modal
      v-if="contact && showModalActivity"
      ref="create-activity"
      :key="'create-activity' + keyActivity + (selectedActivity ? selectedActivity.id : '')"
      :activity-detail="selectedActivity"
      :contact="contact"
      :contact-persons="activatedContactPerson"
      :ids="referencesContactIds"
      @activity-created="loadActivities"
      @activity-updated="loadActivities"
      @activity-deleted="activityDeleted" />
    <add-document-modal
      v-if="contact !== null && contact !== undefined && contact && contact.ownerId"
      :suffix="'contactDocument'"
      :owner-id="contact ? contact.ownerId : 0"
      :contact="contact"
      :contact-persons="typeOfContact === 'person' ? relationsContacts : []"
      :show-in-portal-checkbox="false"
      @document-created="documentCreated" />
    <contact-person-creation
      v-if="selectedPerson !== null || selectedCompany !== null"
      ref="contact-person-modal"
      :key="selectedPerson !== null ? 'modal-person'+selectedPerson.id : 'modal-person'"
      :company-contact="selectedCompany"
      :contact-type="typeOfContact"
      :selected-contact="selectedPerson"
      @hide-modal="hideContactPersonModal"
      @new-contact-person="contactPersonCreated"
      @edit-contact-person="contactPersonEdited"
      @delete-contact-person="contactPersonDeleted" />
    <send-brief-butler-modal
      ref="brief-butler-modal" />
    <add-address-modal
      :contact="contact"
      :contact-persons="typeOfContact === 'person' ? relationsContacts : contact ? contact.contactPersons : []"
      :item-to-edit="itemToEdit"
      @address-added="addressAdded"
      @clear-modal="clearModal" />
    <b-modal
      :id="'confirmationModalLinkContact'"
      ref="confirmationModalLinkContact"
      data-cy="contact-unlink-confirmation-modal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.contact.linkContact.title')">
      <div class="col">
        <p>{{ $t('message.contact.linkContact.unlink') }}</p>
      </div>
      <div class="col">
        <coozzy-button
          data-cy="contact-unlink-cancel-button"
          size="small"
          class="mb-0 border"
          design="transparent"
          @click="$bvModal.hide('confirmationModalLinkContact')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          data-cy="contact-unlink-confirm-button"
          size="small"
          class="float-right mb-0"
          design="green"
          :disabled="disabledLinkBtn"
          @click="unlinkPersons()">
          {{ $t('message.generic.confirm') }}
        </coozzy-button>
      </div>
    </b-modal>
    <digital-signature-modal
      v-if="showDigitalSignature"
      ref="digitalSignatureModal"
      :contacts="contactsList"
      :read-only="readOnlyDigitSign"
      :tenancy="tenancySignRequest"
      :signature-request="selectedSignRequest"
      @cancel-signature="signatureRequestCanceled"
      @hide="hideDigitalSignatureModal" />
  </section>
</template>

<script lang="ts">
import ContactApi from '../../misc/apis/ContactApi'
import MediaApi from '../../misc/apis/MediaApi'
import MessageApi from '../../misc/apis/MessageApi'
import AddDocumentModal from '@/properties/components/AddDocumentModal.vue'
import Document from '@/properties/components/Document.vue'
import CoozzySpinner from '../../framework/components/misc/CoozzySpinner.vue'
import ContactEventCard from './ContactEventCard.vue'
import ContactReminderCard from './ContactReminderCard.vue'
import TenanciesContactCard from '@/contact/components/TenanciesContactCard.vue'
import BuildingListContactCard from '@/contact/components/BuildingListContactCard.vue'
import CondominiumContactCard from '@/contact/components/CondominiumContactCard.vue'
import ContactDetailCard from './ContactDetailCard.vue'
import ContactPersonCreation from '@/contact/components/ContactPersonCreation.vue'
import SmallMessagesTable from '@/messages/SmallMessagesTable.vue'
import CreateActivityModal from './CreateActivityModal.vue'
import CoozzyDetailCard from '@/framework/components/card/CoozzyDetailCard.vue'
import CoozzyPhoneIcon from '../../framework/components/icons/CoozzyPhoneIcon.vue'
import CoozzyPaperClipIcon from '../../framework/components/icons/CoozzyPaperClipIcon.vue'
import CoozzyMobileAltIcon from '../../framework/components/icons/CoozzyMobileAltIcon.vue'
import CoozzyActivityIcon from '../../framework/components/icons/CoozzyActivityIcon.vue'
import CoozzyContactPersonIcon from '../../framework/components/icons/CoozzyContactPersonIcon.vue'
import CoozzyAddressCardIcon from '../../framework/components/icons/CoozzyAddressCardIcon.vue'
import CoozzyCalendarCheckIcon from '../../framework/components/icons/CoozzyCalendarCheckIcon.vue'
import CoozzyCheckMarkIcon from '../../framework/components/icons/CoozzyCheckMarkIcon.vue'
import CoozzyDocumentIcon from '../../framework/components/icons/CoozzyDocumentIcon.vue'
import CoozzyReminderIcon from '../../framework/components/icons/CoozzyReminderIcon.vue'
import CoozzyAngleDoubleUpIcon from '../../framework/components/icons/CoozzyAngleDoubleUpIcon.vue'
import CoozzyAngleDoubleDownIcon from '../../framework/components/icons/CoozzyAngleDoubleDownIcon.vue'
import Vue, { PropType } from 'vue'
import CoozzyPageTitle from '../../framework/layout/CoozzyPageTitle.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { formatting } from '@/mixins/formatting'
import { user } from '@/mixins/user'
import { contact } from '@/mixins/contact'
import { eventBus, mail } from '@/mixins/mail'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import AddAddressModal from '@/contact/components/AddAddressModal.vue'
import ContactSearchProvider from '@/framework/components/search/ContactSearchProvider.vue'
import { VueAutosuggest } from 'vue-autosuggest'
import AddressText from '@/framework/components/misc/AddressText.vue'
import { routeChecks } from '@/mixins/routeChecks'
import CreateContactModal from '@/contact/components/CreateContactModal.vue'
import UserApi from '@/misc/apis/UserApi'
import ObjectApi from '@/misc/apis/ObjectApi'
import SendBriefButlerModal from '@/platform/customers/SendBriefButlerModal.vue'
import CoozzyFavoriteIcon from '@/framework/components/icons/CoozzyFavoriteIcon.vue'
import CoozzySuccessIcon from '@/framework/components/icons/CoozzySuccessIcon.vue'
import { responsivity } from '@/mixins/responsivity'
import DeepSignApi from '@/misc/apis/DeepSignApi'
import DigitalSignatureModal from '@/user/DigitalSignatureModal.vue'
import CoozzyEditIcon from '@/framework/components/icons/CoozzyEditIcon.vue'
import CoozzyTicketIcon from '@/framework/components/icons/CoozzyTicketIcon.vue'
import CoozzyBriefButlerIcon from '@/framework/components/icons/CoozzyBriefButlerIcon.vue'
import { media } from '@/mixins/media'
import { v4 as uuidv4 } from 'uuid'
import TicketContactCard from '@/contact/components/TicketContactCard.vue'
import {
  ObjectobjectObject,
  ObjecttenancyTenancy,
  V2Person,
  Contactcontactv2Company,
  Activityv3Activity, CondominiumCondominium, MediadocumentDocument, CommondeepsignSignRequest
} from '@/types/api'
import { Contact } from '@/types/models'

interface AutosuggestSuggestion {
  item?: {
    firstName?: string;
    lastName?: string;
    address?: unknown;
    owner?: {
      name?: string;
    };
  };
}

export default Vue.extend({
  name: 'ContactDetailView',
  components: {
    CoozzyBriefButlerIcon,
    CoozzyTicketIcon,
    CoozzyEditIcon,
    DigitalSignatureModal,
    CoozzySuccessIcon,
    CreateContactModal,
    CoozzyButton,
    SmallMessagesTable,
    ContactDetailCard,
    ContactEventCard,
    ContactReminderCard,
    ContactPersonCreation,
    CoozzyPageTitle,
    CreateActivityModal,
    AddDocumentModal,
    Document,
    CoozzyDetailCard,
    CoozzyMobileAltIcon,
    CoozzyCalendarCheckIcon,
    CoozzyCheckMarkIcon,
    CoozzyReminderIcon,
    CoozzyPaperClipIcon,
    CoozzyDocumentIcon,
    CoozzyPhoneIcon,
    CoozzyActivityIcon,
    CoozzyContactPersonIcon,
    CoozzySpinner,
    CoozzyAngleDoubleUpIcon,
    CoozzyAngleDoubleDownIcon,
    CoozzyAddressCardIcon,
    AddAddressModal,
    SendBriefButlerModal,
    VueAutosuggest,
    AddressText,
    CoozzyFavoriteIcon,
    TenanciesContactCard,
    BuildingListContactCard,
    CondominiumContactCard,
    TicketContactCard,
    ContactSearchProvider
  },
  mixins: [user, formatting, contact, routeChecks, mail, responsivity, media],
  props: {
    id: {
      type: String as PropType<string>,
      default: ''
    }
  },
  metaInfo() {
    return {
      title: `${this.$t('message.navigation.contact.title')} ${this.contactName}`
    }
  },
  data() {
    return {
      listbuilding: [] as ObjectobjectObject[],
      refreshAutosuggestKey: '',
      loading: false,
      keyActivity: '',
      contact: null as Contact | null,
      contactToEdit: null as Contact | null,
      emailSuggestion: '',
      debounceTimeout: null,
      inputProps: {
        placeholder: this.$t('message.generic.search'),
        class: 'form-control form-control-sm',
        id: 'autosuggest__input'
      },
      referenceIds: [] as string[],
      isBusyActivity: false,
      employeeList: [],
      reminderList: [],
      activityList: [] as Activityv3Activity[],
      activityEmployeeList: [],
      mailList: [],
      filterFolder: 'FILTER_FOLDER_ALL',
      totalPageMailCount: 1,
      totalMailsCount: 0,
      contactOwnerAccount: null,
      contactRelationOwnerAccount: [],
      showAllContacts: false,
      showAllActivities: false,
      showAllDocuments: false,
      showAllRentalDocuments: false,
      showCondominiumDocuments: false,
      contactDocuments: [] as MediadocumentDocument[],
      visibleContactDocuments: [] as MediadocumentDocument[],
      rentalDocuments: [] as MediadocumentDocument[],
      condominiumDocs: [] as MediadocumentDocument[],
      relationsContacts: [] as Contact[],
      selectedPerson: null as Contact | null,
      selectedCompany: null as Contact | null,
      selectedEmail: null,
      emailToReply: null,
      selectedActivity: null as Activityv3Activity | null,
      typeOfContact: '',
      elementChanged: null,
      uniqueId: '',
      disabledLinkBtn: false,
      emailToContact: null,
      listTenancies: [] as ObjecttenancyTenancy[],
      listCondominiums: [] as CondominiumCondominium[],
      fieldsActivity: [
        {
          key: 'date',
          label: this.$t('message.calendar.reminder.date'),
          tdClass: 'align-middle'
        },
        {
          key: 'subject',
          label: this.$t('message.calendar.reminder.subject'),
          tdClass: 'align-middle max-w-subject'
        },
        {
          key: 'employee',
          label: this.$t('message.calendar.reminder.employee'),
          tdClass: 'align-middle'
        },
        {
          key: 'billable',
          label: this.$t('message.tableColumnHeadings.billable'),
          tdClass: 'align-middle text-center',
          thClass: 'text-center'
        },
        {
          key: 'hourlyRate',
          label: this.$t('message.tableColumnHeadings.hourlyRate'),
          tdClass: 'align-middle text-right',
          thClass: 'text-right'
        },
        {
          key: 'duration',
          label: this.$t('message.tableColumnHeadings.duration'),
          tdClass: 'align-middle'
        },
        {
          key: 'amountChf',
          label: this.$t('message.tableColumnHeadings.amountChf'),
          tdClass: 'align-middle text-right',
          thClass: 'text-right'
        }
      ],
      fieldsContactPersons: [
        {
          key: 'name',
          label: this.$t('message.generic.name'),
          tdClass: 'align-middle p-md-0'
        },
        {
          key: 'phoneNumber',
          label: this.$t('message.generic.phoneNumber'),
          tdClass: 'align-middle phone-cell'
        },
        {
          key: 'email',
          label: this.$t('message.generic.email'),
          tdClass: 'align-middle'
        },
        {
          key: 'action',
          label: '',
          tdClass: 'align-middle align-icon'
        }
      ],
      itemToEdit: null,
      hasEmails: false,
      showModalActivity: false,
      isBusySignature: false,
      showAllSignatures: false,
      fieldsSignature: [
        {
          key: 'created',
          label: this.$t('message.deepSign.created'),
          thClass: 'th-active',
          tdClass: 'align-middle border-right-0'
        },
        {
          key: 'contacts',
          label: this.$t('message.deepSign.contacts'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'documentType',
          label: this.$t('message.deepSign.documentType'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'Signers',
          label: this.$t('message.deepSign.Signers'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'status',
          label: this.$t('message.deepSign.status.title'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        }
      ],
      signatureRequests: [] as CommondeepsignSignRequest[],
      showPopover: false,
      selectedPopOverEvent: null as CommondeepsignSignRequest | null,
      page: {
        left: 0,
        top: 0
      },
      contactsList: [] as Contact[],
      selectedSignRequest: null as CommondeepsignSignRequest | null,
      tenancySignRequest: null as ObjecttenancyTenancy | null,
      readOnlyDigitSign: false,
      showDigitalSignature: false,
      contactLoadRequestKey: 0
    }
  },
  computed: {
    ...mapGetters('message', ['getSentMails', 'getMailboxesForUserId']),
    currentUserMailboxIds() {
      return this.getMailboxesForUserId(this.userId, false).map(mailbox => mailbox.id)
    },
    lisfOfBuildings() {
      return this.listbuilding
    },
    countSignRequests() {
      return this.signatureRequests.length
    },
    contactSearchFilters() {
      return { isOwner: false, active: true, isEmployee: false, isOnlyInterestedParty: false }
    },
    contactOwnerName() {
      const acc = this.contactOwnerAccount ? this.contactOwnerAccount : null

      if (acc) {
        if (acc.company) {
          return acc.company.name
        } else if (acc.individual) {
          return acc.individual.firstName + ' ' + acc.individual.lastName
        }
      }

      return ''
    },
    contactName() {
      return this.contact && this.contact.name ? this.contact.name : ''
    },
    contactPerson() {
      if (this.contact && this.countContactPerson !== 0 && this.typeOfContact === 'company') {
        const cp = this.contact.contactPersons
        return cp.sort(function (a, b) {
          return b.active - a.active
        })
      }
      return []
    },
    activatedContactPerson() {
      if (this.typeOfContact === 'company') {
        return this.contact.contactPersons.filter(x => x.active)
      } else {
        return this.relationsContacts.filter(x => x.active)
      }
    },
    contactPersonsList() {
      if (this.typeOfContact === 'company') {
        return this.contact.contactPersons
      } else {
        return this.relationsContacts
      }
    },
    availableEmployees() {
      const array = []
      if (this.employeeList.length !== 0) {
        this.employeeList.forEach((emp) => {
          if (typeof (emp) !== 'undefined') {
            array.push({
              label: (emp.profile.firstName ? emp.profile.firstName.substr(0, 1) + '. ' : '') + emp.profile.lastName + (emp.deleted ? ` (${this.$t('message.generic.deleted')})` : ''),
              value: emp.id
            })
          }
        })
      }
      return array
    },
    referencesContactIds() {
      let arr = []
      if (this.contact?.id) {
        arr = [this.contact.id]
        if (this.typeOfContact === 'person') {
          return arr.concat(this.relationsContacts.map(d => d.id))
        }
      }
      return arr
    },
    availableAttendees() {
      const array = []
      if (this.contact !== null) {
        const cont = {
          firstName: this.contact.firstName,
          lastName: this.contact.lastName,
          name: this.contact.name,
          email: this.contact.email
        }
        array.push(cont)
        if (this.contact.contactPersons !== undefined && this.contact.contactPersons.length > 0) {
          this.contact.contactPersons.filter(x => x.active).forEach(element => {
            const cont = {
              firstName: element.firstName,
              lastName: element.lastName,
              name: element.name,
              email: element.email
            }
            array.push(cont)
          })
        }
        if (this.employeeList.length !== 0) {
          this.employeeList.forEach(element => {
            const cont = {
              firstName: element.profile.firstName,
              lastName: element.profile.lastName,
              email: element.email
            }
            array.push(cont)
          })
        }
      }
      return array
    },
    availableContact() {
      let result = [this.contact].concat(this.relationsContacts)

      if (this.contact.contactPersons && this.contact.contactPersons.length > 0) {
        result = result.concat(this.contact.contactPersons)
      }

      return result.filter(x => x.active)
    },
    countActivity() {
      return this.activityList.length
    },
    countRelationsContacts() {
      return this.relationsContacts.length
    },
    countContactPerson() {
      if (this.contact !== null && this.contact.contactPersons) {
        return this.contact.contactPersons.length
      }
      return 0
    },
    countDocument() {
      let count = 0
      if (this.visibleContactDocuments) {
        count = this.visibleContactDocuments.length
      }
      return count
    },
    isExternalEmployee() {
      return this.contact && parseInt(this.contact.ownerId) !== parseInt(this.accountId) && (this.contact.types || []).includes('EMPLOYEE')
    },
    listDocuments() {
      const docs = [...this.visibleContactDocuments]
      return docs.sort((a, b) => (a.name > b.name ? 1 : -1))
    },
    listRentalDocuments() {
      const docs = this.rentalDocuments
      return docs.sort((a, b) => b.contractNumber - a.contractNumber)
    },
    listCondominiumDocuments() {
      const docs = this.condominiumDocs
      return docs.sort((a, b) => b.contractNumber - a.contractNumber)
    }
  },
  watch: {
    id: function (newVal, oldVal) {
      if (newVal !== oldVal) {
        // clear contact data when pushing to the same route with different contact id
        this.clearContactData()
        this.loadContact()
      }
    },
    getSentMails: function (newVal) {
      if (newVal.length > 0) {
        this.reloadSmallMessagesData()
      }
    },
    currentUserMailboxIds: function (newVal, oldVal) {
      // Mailboxes can finish loading after the contact, which previously left
      // loadMails() with an empty mailboxIds array and the section hidden.
      // Retry the fetch once mailboxes become available.
      if (newVal.length > 0 && (!oldVal || oldVal.length === 0) && this.referenceIds.length > 0 && !this.hasEmails) {
        this.loadMails()
      }
    },
    elementChanged: function () {
      this.loadContact()
    }
  },
  beforeDestroy() {
    eventBus.$off('archive-mail')
    eventBus.$off('restore-mail')
    eventBus.$off('deleteMail')
    eventBus.$off('refresh-list')
    eventBus.$off('tenant-change-rental-documents-added')
  },
  mounted: function () {
    document.body.onmousedown = function (e) {
      if (e.button === 1) {
        e.preventDefault()
        return false
      }
    }
    this.loadContact()
    this.loadMailboxesForUserId(this.userId)
    eventBus.$on('archive-mail', () => {
      this.loadMails()
    })
    eventBus.$on('restore-mail', () => {
      this.loadMails()
    })
    eventBus.$on('deleteMail', () => {
      this.loadMails()
    })
    eventBus.$on('refresh-list', () => {
      this.loadMails()
    })
    eventBus.$on('tenant-change-rental-documents-added', this.onTenantChangeRentalDocumentsAdded)
  },
  methods: {
    ...mapMutations('message', [
      'setSendMailContacts',
      'setSendMailHasContext',
      'setSubject',
      'setEmailToForwardAsAttachment',
      'setSendMailPreselectedContact',
      'setSendMailReferenceIds',
      'setSendMailEmailToReply'
    ]),
    ...mapActions('message', ['loadMailboxesForUserId']),
    getSuggestionContactName(suggestion: unknown) {
      const typedSuggestion = suggestion as AutosuggestSuggestion
      const firstName = typedSuggestion?.item?.firstName || ''
      const lastName = typedSuggestion?.item?.lastName || ''
      return `${firstName} ${lastName}`.trim()
    },
    getSuggestionAddress(suggestion: unknown) {
      const typedSuggestion = suggestion as AutosuggestSuggestion
      return typedSuggestion?.item?.address || null
    },
    getSuggestionOwnerName(suggestion: unknown) {
      const typedSuggestion = suggestion as AutosuggestSuggestion
      return typedSuggestion?.item?.owner?.name || ''
    },
    hasContactType(targetContact: Contact | null, type: string) {
      return (targetContact?.types || []).some(contactType => String(contactType) === type)
    },
    openBriefButlerModal() {
      const briefButlerModalRef = this.$refs['brief-butler-modal'] as Vue & { show?: () => void }
      briefButlerModalRef?.show?.()
    },
    fillBuildings(buildings) {
      this.listbuilding = buildings
    },
    downloadContactDocuments(_event?: unknown) {
      const idsAndNames = this.listDocuments.map(x => ({
        id: x.versions[x.versions.length - 1].mediaId,
        mediumName: x.name
      }))
      const nameOfMergedFile = this.contact.name + (this.countRelationsContacts > 0 ? (', ' + this.relationsContacts.map(x => x.name).join(', ')) : '')
      this.mergeDownloadAllDocuments(idsAndNames, nameOfMergedFile)
    },
    getDocumentLatestMediaId(document) {
      if (!document?.versions || document.versions.length === 0) {
        return null
      }
      const latestVersion = document.versions[document.versions.length - 1]
      return latestVersion?.mediaId || null
    },
    getTenancyReferenceId(attachToReferenceIds = []) {
      return (attachToReferenceIds || []).find(ref => ref && ref.startsWith('tenancy_')) || null
    },
    getCompletedDeepSignMediaIdsForTenancies() {
      return new Set(
        (this.signatureRequests || [])
          .filter(request => {
            const hasTenancyReference = (request.attachToReferenceIds || []).some(ref => ref && ref.startsWith('tenancy_'))
            return hasTenancyReference && request.completed && request.signedMediaId
          })
          .map(request => request.signedMediaId)
      )
    },
    updateVisibleContactDocuments() {
      const rentalDocIds = new Set(this.rentalDocuments.map(x => x.id))
      const rentalMediaIds = new Set(
        this.rentalDocuments
          .map(x => this.getDocumentLatestMediaId(x))
          .filter(x => x)
      )
      const completedDeepSignMediaIds = this.getCompletedDeepSignMediaIdsForTenancies()
      this.visibleContactDocuments = this.contactDocuments.filter(x => {
        const latestMediaId = this.getDocumentLatestMediaId(x)
        const isInRentalDocuments = rentalDocIds.has(x.id) || (latestMediaId && rentalMediaIds.has(latestMediaId))
        const isCompletedDeepSignSignedDoc = latestMediaId && completedDeepSignMediaIds.has(latestMediaId)
        return !isInRentalDocuments && !isCompletedDeepSignSignedDoc
      })
    },
    downloadRentalDocuments(_event?: unknown) {
      const idsAndNames = this.listRentalDocuments.map(x => ({
        id: x.versions[x.versions.length - 1].mediaId,
        mediumName: x.name
      }))
      const nameOfMergedFile = this.contact.name + (this.countRelationsContacts > 0 ? (', ' + this.relationsContacts.map(x => x.name).join(', ')) : '')
      this.mergeDownloadAllDocuments(idsAndNames, nameOfMergedFile)
    },
    downloadCondominiumDocuments(_event?: unknown) {
      const idsAndNames = this.listCondominiumDocuments.map(x => ({
        id: x.versions[x.versions.length - 1].mediaId,
        mediumName: x.name
      }))
      const nameOfMergedFile = this.contact.name + (this.countRelationsContacts > 0 ? (', ' + this.relationsContacts.map(x => x.name).join(', ')) : '')
      this.mergeDownloadAllDocuments(idsAndNames, nameOfMergedFile)
    },
    onRowClicked(item, index, event) {
      if (event.srcElement.classList.contains('contactName') || event.srcElement.classList.contains('contactName') || event.srcElement.classList.contains('contractNumber') || event.srcElement.classList.contains('contractNumber')) {
        event.preventDefault()
      } else {
        this.selectedSignRequest = item
        this.readOnlyDigitSign = true
        const tenancyId = this.getTenancyReferenceId(item.attachToReferenceIds || [])
        if (tenancyId) {
          this.tenancySignRequest = this.listTenancies.find(x => x.id === tenancyId) || null
        }
        this.showDigitalSignatureModal()
      }
    },
    showDigitalSignatureModal() {
      this.showDigitalSignature = true
      this.readOnlyDigitSign = true
      this.$nextTick(() => {
        this.$refs.digitalSignatureModal?.show()
      })
    },
    signatureRequestCanceled(id) {
      this.signatureRequests.map((item) => {
        if (item.id === id) {
          item.cancelled = true
        }
        return true
      })
    },
    hideDigitalSignatureModal() {
      this.showDigitalSignature = false
      this.readOnlyDigitSign = false
      this.selectedSignRequest = null
      this.tenancySignRequest = null
    },
    redirectToDetailPage(contactId, newTab = false) {
      let name = ''
      if (this.isOwnerModule) {
        name = 'OwnerContactDetailsView'
      } else if (this.isAssetModule) {
        name = 'AssetContactDetailsView'
      } else if (this.isAdminModule) {
        name = 'AdminContactDetailsView'
      } else if (this.isAccountingModule) {
        name = 'AccountingContactDetailsView'
      } else {
        name = 'MarketingContactDetailsView'
      }
      if (newTab) {
        const routerHref = this.$router.resolve({
          name: name,
          params: { id: contactId }
        }, () => {
        })
        window.open(routerHref.href, '_blank')
      } else {
        this.$router.push({
          name: name,
          params: { id: contactId }
        }, () => {
        })
      }
    },
    getSignerName(signer) {
      const contact = this.contactsList.find(c => c.id === signer.contactId)
      if (contact) {
        return contact.name
      }
      return ''
    },
    onMouseMove(e) {
      this.showPopover = this.hasSomeParentTheClass(e.srcElement, 'fieldToShow')
      if (this.showPopover && document.getElementById('eventPopover')) {
        this.page.left = e.layerX
        this.page.top = e.layerY
      }
    },

    mouseLeaveEvents(_event?: unknown) {
      this.selectedPopOverEvent = null
    },
    mouseEnterEvents(event) {
      this.selectedPopOverEvent = event
    },
    getStatus(item) {
      if (item.cancelled) {
        return 'cancelled'
      } else if (item.signers.every((x) => x.signed) || item.completed) {
        return 'complete'
      } else if (item.signers.some(signer => !signer.signed)) {
        return 'incomplete'
      }
    },
    loadSignatureRequest(requestKey = this.contactLoadRequestKey) {
      this.isBusySignature = true
      const refIds = [this.contact.id]
      if (this.contact.contactPersons && this.contact.contactPersons.length > 0) {
        refIds.push(...this.contact.contactPersons.map(x => x.id))
      }
      if (this.listTenancies?.length > 0) {
        refIds.push(...this.listTenancies.map(t => t.id))
      }
      DeepSignApi.listSignRequestsByAttachToReferenceIds(refIds).then((response) => {
        if (requestKey !== this.contactLoadRequestKey) {
          return
        }
        this.signatureRequests = response.signRequests
        this.updateVisibleContactDocuments()
        this.isBusySignature = false
        if (response.signRequests.length > 0) {
          let contactIds = []
          response.signRequests.forEach((signRequest) => {
            if (signRequest.completed === false && signRequest.cancelled === false) {
              this.hasSignatureRequestIncomplete = true
            }
            contactIds = contactIds.concat(signRequest.signers.map((signer) => {
              return signer.contactId
            }))
          })
          contactIds = [...new Set(contactIds)]
          contactIds = contactIds.filter((id) => {
            return !this.contactsList.some((contact) => {
              return contact.id === id
            })
          })
          if (contactIds.length > 0) {
            const uniqIds = [...new Set(contactIds)]
            if (uniqIds.length > 0) {
              ContactApi.contactResolve(uniqIds)
                .then(responseCon => {
                  if (requestKey !== this.contactLoadRequestKey) {
                    return
                  }
                  this.contactsList = this.contactsList.concat(responseCon.persons).concat(responseCon.companies)
                })
                .catch(e => {
                  console.error(e)
                })
            }
          }
        }
      }).catch((e) => {
        console.log(e)
        if (requestKey !== this.contactLoadRequestKey) {
          return
        }
        this.isBusySignature = false
      })
    },
    onApplicationFormAllInfoAccepted() {
      this.$refs['contact-detail-card']?.onApplicationFormAllInfoAccepted()
    },
    searchEmails(filterFolder, mailBoxId, more, query) {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout)
      }
      this.debounceTimeout = setTimeout(() => {
        this.debounceTimeout = null
        this.loadMails(filterFolder, mailBoxId, more, query)
      }, 300)
    },
    editAddress(item, user) {
      this.itemToEdit = item
      this.itemToEdit.userId = user?.id || null
      this.$bvModal.show('add-address-modal')
    },
    contactEdited() {
      this.loadContact()
    },
    clearModal() {
      this.itemToEdit = null
    },
    reloadSmallMessagesData() {
      this.loadMails()
    },
    createTicket() {
      if (this.isOwnerModule) {
        this.$router.push({
          name: 'OwnerTicketCreation',
          query: { fromBulk: true, ids: this.id, defaultAssignee: this.contact.ownerId }
        })
      } else if (this.isAdminModule) {
        this.$router.push({
          name: 'AdminTicketCreation',
          query: { fromBulk: true, ids: this.id, defaultAssignee: this.contact.ownerId }
        })
      } else if (this.isAssetModule) {
        this.$router.push({
          name: 'AssetTicketCreation',
          query: { fromBulk: true, ids: this.id, defaultAssignee: this.contact.ownerId }
        })
      } else if (this.isAccountingModule) {
        this.$router.push({
          name: 'AccountingTicketCreation',
          query: { fromBulk: true, ids: this.id, defaultAssignee: this.contact.ownerId }
        })
      } else {
        this.$router.push({
          name: 'MarketingTicketCreation',
          query: { fromBulk: true, ids: this.id, defaultAssignee: this.contact.ownerId }
        })
      }
    },
    getContactOwnerName(id) {
      const acc = this.contactRelationOwnerAccount.find(x => x.id === id)
      if (acc) {
        if (acc.company) {
          return acc.company.name
        } else if (acc.individual) {
          return acc.individual.firstName + ' ' + acc.individual.lastName
        }
      }
      return ''
    },
    loadContact() {
      const requestKey = ++this.contactLoadRequestKey
      this.loading = true
      this.isBusyActivity = true
      this.referenceIds = [this.id]
      this.condominiumDocs = []
      ContactApi.contactResolve([this.id])
        .then(response => {
          if (requestKey !== this.contactLoadRequestKey) {
            return
          }
          this.contact = (response.persons as (Contactcontactv2Company | V2Person)[])
            .concat(response.companies)
            .find(x => x.id === this.id || ('contactPersons' in x && x.contactPersons.some(cp => cp.id === this.id)))
          if (!('contactPersons' in this.contact)) {
            this.typeOfContact = 'person'
          } else if (('contactPersons' in this.contact)) {
            this.typeOfContact = 'company'
            this.referenceIds = [this.contact.id].concat(this.contact.contactPersons.map(d => d.id))
          } else {
            this.$router.go(-1)
            Vue.toasted.show(this.$t('message.loadingErrors.notAllowedContacts'), { type: 'error' })
          }
          if (this.contact.relations.length > 0) {
            this.relationsContacts = (response.persons as (Contactcontactv2Company | V2Person)[])
              .concat(response.companies)
              .filter(c => c.id !== this.contact.id)
            this.referenceIds = this.referenceIds.concat(this.relationsContacts.map(d => d.id))
          }

          this.changeUniqueId()
          this.loadDocuments(requestKey)
          this.loadMails('FILTER_FOLDER_ALL', this.getMailboxesForUserId(this.userId, false).map(mailbox => mailbox.id), false, '', requestKey)
          this.loadActivities(requestKey)
          this.loadOwner(requestKey)
          this.loadRelationContactOwner(requestKey)
          if (!((this.contact.types?.includes('TENANT') || this.contact.types?.includes('SUB_TENANT')) && !this.contact.types?.includes('CONDOMINIUM_OWNER'))) {
            this.loadSignatureRequest(requestKey)
          }
        })
        .catch(e => {
          console.log(e)
          if (requestKey !== this.contactLoadRequestKey) {
            return
          }
          this.isBusyActivity = false
          Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
        })
        .finally(() => {
          if (requestKey !== this.contactLoadRequestKey) {
            return
          }
          this.loading = false
        })
    },
    loadDocuments(requestKey = this.contactLoadRequestKey) {
      let documentIds = this.contact.documentIds
      for (const relationsContact of this.relationsContacts.filter(x => x.active)) {
        documentIds = documentIds.concat(relationsContact.documentIds)
      }

      const uniqueDocumentIds = [...new Set(documentIds)]
      if (uniqueDocumentIds.length > 0) {
        MediaApi.listDocuments(uniqueDocumentIds)
          .then(response => {
            if (requestKey !== this.contactLoadRequestKey) {
              return
            }
            this.contactDocuments = response.documents.reverse()
            this.updateVisibleContactDocuments()
          })
      } else {
        this.contactDocuments = []
        this.updateVisibleContactDocuments()
      }
    },
    loadOwner(requestKey = this.contactLoadRequestKey) {
      // Load the accounts to display owner names
      UserApi.getAccount(this.contact.ownerId).then(response => {
        if (requestKey !== this.contactLoadRequestKey) {
          return
        }
        this.contactOwnerAccount = response.account
      })
    },
    loadRelationContactOwner(requestKey = this.contactLoadRequestKey) {
      // Load the accounts to display owner names
      const relationContactOwnerIds = this.relationsContacts.flatMap(x => x.ownerId)
      if (relationContactOwnerIds.length > 0) {
        UserApi.listAccountsByIds(relationContactOwnerIds).then(response => {
          if (requestKey !== this.contactLoadRequestKey) {
            return
          }
          this.contactRelationOwnerAccount = response.accounts
        })
      }
    },
    getDocumentOwner(documentId) {
      const ownerName = []
      if (this.relationsContacts.length > 0) {
        if (this.contact.documentIds.includes(documentId)) {
          ownerName.push(this.contact.name)
        }
        const owners = this.relationsContacts.filter(obj => obj.documentIds.includes(documentId))
        owners.forEach(element => {
          ownerName.push(element.name)
        })
        return ownerName
      } else {
        return []
      }
    },
    loadMails(filterFolder = 'FILTER_FOLDER_ALL', mailboxIds = this.getMailboxesForUserId(this.userId, false).map(mailbox => mailbox.id), more = false, query = '', requestKey = this.contactLoadRequestKey) {
      if (mailboxIds?.length > 0) {
        this.mailList = []
        MessageApi.listMailsByReferenceIds(this.referenceIds, filterFolder, mailboxIds, 1, more ? this.totalMailsCount : 5, query)
          .then(async response => {
            if (requestKey !== this.contactLoadRequestKey) {
              return
            }
            this.mailList = response.mails
            this.filterFolder = filterFolder
            this.totalPageMailCount = response.totalPageCount
            this.totalMailsCount = response.totalMailsCount
            if (response.mails.length > 0) {
              this.hasEmails = true
              this.loadMAilsContacts(requestKey)
            }
          }).catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.message'), { type: 'error' })
        })
      }
    },
    loadMAilsContacts(requestKey = this.contactLoadRequestKey) {
      const listRefs = this.mailList.map(mail => mail.referenceIds).flat().filter(id => id.includes('contact_'))
      ContactApi.contactResolve(listRefs).then(response => {
        if (requestKey !== this.contactLoadRequestKey) {
          return
        }
        const contacts = this.relationsContacts.concat(response.persons).concat(response.companies)
        this.setContacts(contacts)
      }).catch(e => {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
      })
    },
    activityDeleted(activity) {
      this.$refs['create-activity']?.hide()
      this.loadActivities()
    },
    loadActivities(requestKey = this.contactLoadRequestKey) {
      ContactApi.getActivitiesByReferenceIdsV3(this.referenceIds)
        .then(async response => {
          if (requestKey !== this.contactLoadRequestKey) {
            return
          }
          this.activityList = response.activities
          this.activityList.sort(function (a, b) {
            return b.timestampStart - a.timestampStart
          })
          const employeeIds = this.activityList.flatMap(d => d.employeeContactIds)
          if (employeeIds.length !== 0) {
            const ids = [...new Set(employeeIds)] as string[]
            ContactApi.contactResolve(ids)
              .then(response => {
                if (requestKey !== this.contactLoadRequestKey) {
                  return
                }
                this.activityEmployeeList = response
              }).catch(e => {
              console.log(e)
              Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
            })
          }
        }).catch(e => {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.activity'), { type: 'error' })
      })
        .finally(() => {
          if (requestKey !== this.contactLoadRequestKey) {
            return
          }
          this.isBusyActivity = false
          this.showModalActivity = false
        })
    },
    isDeath(date) {
      return !!(date && new Date().getTime() >= new Date(date.year + '-' + date.month + '-' + date.day).getTime())
    },
    addDocumentClicked(suffix = '') {
      this.$bvModal.show('add-document-modal' + suffix)
    },
    contactCreated(data) {
      if (data.addingAnother !== true && data.takeOverAddress !== true && data.contact) {
        this.redirectToDetailPage(data.contact.id)
      }
    },
    contactUpdated(data) {
      if (data.contact && this.contact.id === data.contact.id) {
        this.contact = data.contact
      } else {
        this.relationsContacts.forEach((value, index) => {
          if (value.id === data.contact.id) {
            Vue.set(this.relationsContacts, index, data.contact)
          }
        })
      }
    },
    contactDeleted() {
      if (this.isOwnerModule) {
        this.$router.push({ name: 'OwnerContactOverview' })
      } else if (this.isAdminModule) {
        this.$router.push({ name: 'AdminContactOverview' })
      } else if (this.isMarketingModule) {
        this.$router.push({ name: 'MarketingContactOverview' })
      } else if (this.isAssetModule) {
        this.$router.push({ name: 'AssetContactOverview' })
      } else if (this.isAccountingModule) {
        this.$router.push({ name: 'AccountingContactOverview' })
      }
    },
    addAddress() {
      this.itemToEdit = null
      this.$bvModal.show('add-address-modal')
    },
    addressAdded() {
      this.itemToEdit = null
      this.loadContact()
    },
    documentCreated(document, suffix, contacts) {
      const contactsIds = contacts.map(x => x.id)
      const promises = []
      contacts.forEach(contact => {
        promises.push(ContactApi.addDocuments(contact.id, [document.id]))
      })

      Promise.all(promises)
        .then(() => {
          if (contacts.filter(x => x.id === this.contact.id).length > 0) {
            this.contact.documentIds.push(document.id)
          }
          this.contactDocuments.unshift(document)
          this.updateVisibleContactDocuments()
          const relationsContacts = this.relationsContacts.find(obj => contactsIds.includes(obj.id))
          if (relationsContacts) {
            relationsContacts.documentIds.push(document.id)
          }
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.document'), { type: 'error' })
        })
    },
    versionAdded(updatedDocument) {
      this.contactDocuments.forEach((doc, index) => {
        if (doc.id === updatedDocument.id) {
          this.contactDocuments.splice(index, 1, updatedDocument)
        }
      }, this)
      this.updateVisibleContactDocuments()
    },
    openActivities() {
      const othersIds = (this.contactPersonsList.concat(this.listTenancies)).map(x => x.id)
      if (this.isOwnerModule) {
        this.$router.push({
          name: 'OwnerActivitiesTicketView',
          params: { id: this.contact?.id },
          query: { view: 'tickets', othersIds: othersIds }
        }, () => {
        })
      } else if (this.isAdminModule) {
        this.$router.push({
          name: 'AdminActivitiesTicketView',
          params: { id: this.contact?.id },
          query: { view: 'tickets', othersIds: othersIds }
        }, () => {
        })
      } else if (this.isAssetModule) {
        this.$router.push({
          name: 'AssetActivitiesTicketView',
          params: { id: this.contact?.id },
          query: { view: 'tickets', othersIds: othersIds }
        }, () => {
        })
      } else if (this.isAccountingModule) {
        this.$router.push({
          name: 'AccountingActivitiesTicketView',
          params: { id: this.contact?.id },
          query: { view: 'tickets', othersIds: othersIds }
        }, () => {
        })
      } else {
        this.$router.push({
          name: 'MarketingActivitiesTicketView',
          params: { id: this.contact.id },
          query: { view: 'tickets', othersIds: othersIds }
        }, () => {
        })
      }
    },
    openActivitiesMiddle() {
      let routeData
      const othersIds = (this.contactPersonsList.concat(this.listTenancies)).map(x => x.id)
      if (this.isOwnerModule) {
        routeData = this.$router.resolve({
          name: 'OwnerActivitiesTicketView',
          params: { id: this.contact?.id },
          query: { view: 'tickets', othersIds: othersIds }
        }, () => {
        })
      } else if (this.isAdminModule) {
        routeData = this.$router.resolve({
          name: 'AdminActivitiesTicketView',
          params: { id: this.contact?.id },
          query: { view: 'tickets', othersIds: othersIds }
        }, () => {
        })
      } else if (this.isAssetModule) {
        routeData = this.$router.resolve({
          name: 'AssetActivitiesTicketView',
          params: { id: this.contact?.id },
          query: { view: 'tickets', othersIds: othersIds }
        }, () => {
        })
      } else if (this.isAccountingModule) {
        routeData = this.$router.resolve({
          name: 'AccountingActivitiesTicketView',
          params: { id: this.contact?.id },
          query: { view: 'tickets', othersIds: othersIds }
        }, () => {
        })
      } else {
        routeData = this.$router.resolve({
          name: 'MarketingActivitiesTicketView',
          params: { id: this.contact.id },
          query: { view: 'tickets', othersIds: othersIds }
        }, () => {
        })
      }
      window.open(routeData.href, '_blank')
    },
    deleteRentalDocument(document) {
      // check if id of document is on listRentalDocuments
      console.log(document)
      ObjectApi.tenancyRemoveDocuments(document.tenancyId, [document.id])
        .then(() => {
          this.listTenancies.find(t => t.id === document.tenancyId)?.documentIds.forEach((doc, index) => {
            if (doc === document.id) {
              this.listTenancies.find(t => t.id === document.tenancyId).documentIds.splice(index, 1)
            }
          }, this)
          this.rentalDocuments.forEach((doc, index) => {
            if (doc.id === document.id) {
              this.rentalDocuments.splice(index, 1)
            }
          }, this)
          this.updateVisibleContactDocuments()
        }).catch(e => {
        Vue.toasted.show(this.$t('message.savingErrors.document'), { type: 'error' })
        console.log(e)
      })
    },
    deleteCondominiumDocuments(document) {
      // check if id of document is on condominiumDocs
      console.log(document)
      ObjectApi.condominiumRemoveDocuments(document.condominiumId, [document.id])
        .then(() => {
          this.listCondominiums.find(t => t.id === document.condominiumId)?.documentIds.forEach((doc, index) => {
            if (doc === document.id) {
              this.listCondominiums.find(t => t.id === document.condominiumId).documentIds.splice(index, 1)
            }
          }, this)
          this.condominiumDocs.forEach((doc, index) => {
            if (doc.id === document.id) {
              this.condominiumDocs.splice(index, 1)
            }
          }, this)
        }).catch(e => {
        Vue.toasted.show(this.$t('message.savingErrors.document'), { type: 'error' })
        console.log(e)
      })
    },
    deleteDocument(document) {
      let contactId = null
      if (this.relationsContacts.length > 0) {
        if (this.contact.documentIds.includes(document.id)) {
          contactId = this.contact.id
        }
        const owner = this.relationsContacts.find(obj => obj.documentIds.includes(document.id))
        if (owner && !contactId) {
          contactId = owner.id
        }
      } else {
        contactId = this.contact.id
      }
      ContactApi.deleteDocuments(contactId, document.id)
        .then(() => {
          this.contact.documentIds.forEach((doc, index) => {
            if (doc === document.id) {
              this.contact.documentIds.splice(index, 1)
            }
          }, this)
          this.contactDocuments.forEach((doc, index) => {
            if (doc.id === document.id) {
              this.contactDocuments.splice(index, 1)
            }
          }, this)
          this.updateVisibleContactDocuments()
        }).catch(e => {
        Vue.toasted.show(this.$t('message.savingErrors.document'), { type: 'error' })
        console.log(e)
      })
    },
    getPhoneNumbers(phoneNumbers, type) {
      if (phoneNumbers && phoneNumbers.length > 0) {
        if (phoneNumbers.find(element => element.type === type)) {
          return phoneNumbers.find(element => element.type === type).number
        }
      }
      return '-'
    },
    createReminderClicked(_item: unknown = null) {
      this.$refs['contact-reminder-card'].createReminderClicked()
    },
    createEventClicked(_item: unknown = null) {
      if (this.$route.name === 'MarketingContactDetailsView') {
        this.$router.push({
          name: 'MarketingCalendarOverview',
          params: { viewCalendar: 'week' },
          query: { referenceIds: this.referenceIds.toString(), fromPage: 'contact' }
        })
      } else if (this.$route.name === 'AdminContactDetailsView') {
        this.$router.push({
          name: 'AdminCalendarOverview',
          params: { viewCalendar: 'week' },
          query: { referenceIds: this.referenceIds.toString(), fromPage: 'contact' }
        })
      } else if (this.$route.name === 'AssetContactDetailsView') {
        this.$router.push({
          name: 'AssetCalendarOverview',
          params: { viewCalendar: 'week' },
          query: { referenceIds: this.referenceIds.toString(), fromPage: 'contact' }
        })
      } else if (this.$route.name === 'AccountingContactDetailsView') {
        this.$router.push({
          name: 'AccountingCalendarOverview',
          params: { viewCalendar: 'week' },
          query: { referenceIds: this.referenceIds.toString(), fromPage: 'contact' }
        })
      } else {
        this.$router.push({
          name: 'OwnerCalendarOverview',
          params: { viewCalendar: 'week' },
          query: { referenceIds: this.referenceIds.toString(), fromPage: 'contact' }
        })
      }
    },
    openSerialEmail() {
      const uniqKey = uuidv4()
      localStorage.setItem(uniqKey, this.contact.id)
      this.$nextTick(() => {
        if (this.isOwnerModule) {
          this.$router.push({
            name: 'OwnerSerialLetterProcessView',
            query: {
              source: 'contact',
              uuid: uniqKey,
              singleLetter: 'true',
              previousRoute: this.$route.fullPath
            }
          })
        } else if (this.isMarketingModule) {
          this.$router.push({
            name: 'MarketingSerialLetterProcessView',
            query: {
              source: 'contact',
              uuid: uniqKey,
              singleLetter: 'true',
              previousRoute: this.$route.fullPath
            }
          })
        } else if (this.isAdminModule) {
          this.$router.push({
            name: 'AdminSerialLetterProcessView',
            query: {
              source: 'contact',
              uuid: uniqKey,
              singleLetter: 'true',
              previousRoute: this.$route.fullPath
            }
          })
        } else if (this.isAssetModule) {
          this.$router.push({
            name: 'AssetSerialLetterProcessView',
            query: {
              source: 'contact',
              uuid: uniqKey,
              singleLetter: 'true',
              previousRoute: this.$route.fullPath
            }
          })
        } else if (this.isAccountingModule) {
          this.$router.push({
            name: 'AccountingSerialLetterProcessView',
            query: {
              source: 'contact',
              uuid: uniqKey,
              singleLetter: 'true',
              previousRoute: this.$route.fullPath
            }
          })
        }
      })
    },
    openSendMailModal() {
      let contactsWithEmail = null
      if ('relations' in this.contact) {
        contactsWithEmail = this.relationsContacts.filter(contactPersons => contactPersons.email !== '')
        if (this.typeOfContact === 'company') {
          contactsWithEmail = contactsWithEmail.concat(this.contact.contactPersons.filter(contactPersons => contactPersons.email !== ''))
        }
      } else {
        contactsWithEmail = this.contact.contactPersons.filter(contactPersons => contactPersons.email !== '')
      }
      const activeContactsWithEmail = contactsWithEmail.filter(x => x.active)
      const companyHasEmail = this.contact.email && this.contact.email !== ''
      if (companyHasEmail || activeContactsWithEmail.length > 0) {
        if (companyHasEmail) {
          this.setSendMailPreselectedContact(this.contact)
          this.setSendMailContacts([this.contact])
        } else {
          this.setSendMailContacts(activeContactsWithEmail)
        }
        this.setSendMailReferenceIds([])
        this.setSendMailEmailToReply(null)
        const toAllTo = !companyHasEmail && activeContactsWithEmail.length > 1
        this.$nextTick(function () {
          this.callShowSendMailModal(toAllTo)
        })
      } else {
        Vue.toasted.show(this.$t('message.loadingErrors.contactHasNoEmail'), { type: 'error' })
      }
    },
    showEmail(item) {
      this.selectedEmail = item
      this.emailToReply = null
      this.setMailDetailMailObject(item)
      this.$nextTick(function () {
        this.callShowDetailMailModal()
      })
    },
    getEmployeeById(employeeId) {
      if (this.activityEmployeeList.length === 0) return '-'
      return this.activityEmployeeList.persons.find(object => object.id === employeeId) || '-'
    },
    getDuration(timestampStart, timestampEnd) {
      if (!timestampStart || !timestampEnd) {
        return '-'
      }
      const start = new Date(parseInt(timestampStart) * 1000)
      const end = new Date(parseInt(timestampEnd) * 1000)
      const diff = end.getTime() - start.getTime()
      if (!Number.isFinite(diff) || diff <= 0) {
        return '-'
      }
      return `${this.$options.filters.timestampToTimeOnly(diff / 1000)}`
    },
    getDurationInSeconds(timestampStart, timestampEnd) {
      if (!timestampStart || !timestampEnd) {
        return 0
      }
      const start = parseInt(timestampStart) * 1000
      const end = parseInt(timestampEnd) * 1000
      if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
        return 0
      }
      return Math.floor((end - start) / 1000)
    },
    getDurationInHours(timestampStart, timestampEnd) {
      return this.getDurationInSeconds(timestampStart, timestampEnd) / 3600
    },
    getAmountInChf(activity) {
      const hourlyRate = Number(activity?.hourlyRate || 0)
      const durationHours = this.getDurationInHours(activity?.timestampStart, activity?.timestampEnd)
      if (!Number.isFinite(hourlyRate) || !Number.isFinite(durationHours)) {
        return 0
      }
      return Math.round(hourlyRate * durationHours * 100) / 100
    },
    formatAmount(value) {
      return this.$options.filters.formatPrice(Math.round((Number(value) || 0) * 100) / 100, 'CHF')
    },
    formatAmountInChf(activity) {
      return this.formatAmount(this.getAmountInChf(activity))
    },
    formatHourlyRate(hourlyRate) {
      const numericRate = Number(hourlyRate)
      if (!Number.isFinite(numericRate) || numericRate <= 0) {
        return '-'
      }
      return this.formatAmount(numericRate)
    },
    editActivity(item) {
      this.selectedActivity = null
      this.showModalActivity = true
      this.keyActivity = Math.random().toString(36).substr(2, 9)
      this.selectedActivity = item
      this.$nextTick(function () {
        this.$refs['create-activity'].show()
      })
    },
    changeUniqueId() {
      this.uniqueId = Math.random().toString(36).substr(2, 9)
    },
    linkContact() {
      if (this.$route.name === 'MarketingContactDetailsView') {
        this.$router.push({
          name: 'MarketingContactOverview',
          query: { linkContact: this.contact.id, linkContactType: this.typeOfContact }
        })
      } else if (this.$route.name === 'AdminContactDetailsView') {
        this.$router.push({
          name: 'AdminContactOverview',
          query: { linkContact: this.contact.id, linkContactType: this.typeOfContact }
        })
      } else if (this.$route.name === 'AssetContactDetailsView') {
        this.$router.push({
          name: 'AssetContactOverview',
          query: { linkContact: this.contact.id, linkContactType: this.typeOfContact }
        })
      } else if (this.$route.name === 'AccountingContactDetailsView') {
        this.$router.push({
          name: 'AccountingContactOverview',
          query: { linkContact: this.contact.id, linkContactType: this.typeOfContact }
        })
      } else {
        this.$router.push({
          name: 'OwnerContactOverview',
          query: { linkContact: this.contact.id, linkContactType: this.typeOfContact }
        })
      }
    },
    openUnlinkModal(element) {
      this.contactToUnlink = element.id
      this.$refs.confirmationModalLinkContact.show()
    },
    unlinkPersons() {
      this.disabledLinkBtn = true
      if (this.typeOfContact === 'company') {
        ContactApi.unLinkCompanies(this.contactToUnlink, this.contact.id)
          .then(() => {
            this.$refs.confirmationModalLinkContact.hide()
            this.disabledLinkBtn = false
            const t = this
            this.contact.relations = this.contact.relations.filter(function (obj) {
              return obj.incomingContactId !== t.contactToUnlink
            })
            this.relationsContacts = this.relationsContacts.filter(function (obj) {
              return obj.id !== t.contactToUnlink
            })
            this.loadDocuments()
            Vue.toasted.show(this.$t('message.successMessages.contactUnLinked'), { type: 'success' })
          })
          .catch(e => {
            this.disabledLinkBtn = false
            console.log(e)
            Vue.toasted.show(this.$t('message.deleteErrors.contact'), { type: 'error' })
          })
      } else {
        ContactApi.unLinkPersons(this.contactToUnlink, this.contact.id)
          .then(() => {
            this.$refs.confirmationModalLinkContact.hide()
            this.disabledLinkBtn = false
            const t = this
            this.contact.relations = this.contact.relations.filter(function (obj) {
              return obj.incomingContactId !== t.contactToUnlink
            })
            this.relationsContacts = this.relationsContacts.filter(function (obj) {
              return obj.id !== t.contactToUnlink
            })
            this.loadDocuments()
            Vue.toasted.show(this.$t('message.successMessages.contactUnLinked'), { type: 'success' })
          })
          .catch(e => {
            this.disabledLinkBtn = false
            console.log(e)
            Vue.toasted.show(this.$t('message.deleteErrors.contact'), { type: 'error' })
          })
      }
    },
    openContactModal() {
      this.selectedPerson = null
      this.selectedCompany = JSON.parse(JSON.stringify(this.contact))
      this.$nextTick(function () {
        this.$refs['contact-person-modal']?.show()
      })
    },
    createNewContactClicked() {
      this.contactToEdit = null
      this.$bvModal.show('create-contact-modal')
    },
    contactPersonCreated(item) {
      this.contact.contactPersons.push(item)
    },
    showPerson(item) {
      this.selectedPerson = JSON.parse(JSON.stringify(item))
      this.selectedCompany = JSON.parse(JSON.stringify(this.contact))
      this.$nextTick(function () {
        this.$refs['contact-person-modal'].show()
      })
    },
    hideContactPersonModal() {
      this.selectedPerson = null
      this.selectedCompany = null
    },
    contactPersonEdited(item) {
      this.selectedPerson = null
      this.selectedCompany = null
      const indexCP = this.contact.contactPersons.findIndex(cp => cp.id === item.id)
      if (indexCP !== -1) {
        Vue.set(this.contact.contactPersons, indexCP, item)
      }
      this.$refs.contactPersonTable.refresh()
    },
    contactPersonDeleted(id) {
      for (let index = 0; index < this.contact.contactPersons.length; index++) {
        if (this.contact.contactPersons[index].id === id) {
          this.contact.contactPersons.splice(index, 1)
        }
      }
    },
    editContactClicked(contact) {
      this.contactToEdit = contact
      this.$nextTick(function () {
        this.$bvModal.show('create-contact-modal')
      })
    },
    sendEmailModal(account) {
      this.emailToContact = { id: account.id, name: account.name, email: account.email }
      this.setSendMailContacts([this.emailToContact])
      this.setSendMailPreselectedContact(this.emailToContact)
      this.setSendMailEmailToReply(null)
      this.$nextTick(function () {
        this.callShowSendMailModal()
      })
    },
    modalHide() {
      this.contactToEdit = null
    },
    clearContactData() {
      this.contact = null
      this.relationsContacts = []
      this.contactDocuments = []
      this.visibleContactDocuments = []
      this.condominiumDocs = []
      this.typeOfContact = ''
      this.mailList = []
      this.hasEmails = false
      this.totalPageMailCount = 1
      this.totalMailsCount = 0
      this.filterFolder = 'FILTER_FOLDER_ALL'
      this.activityList = []
      this.activityEmployeeList = []
      this.reminderList = []
      this.rentalDocuments = []
      this.listTenancies = []
      this.listCondominiums = []
      this.signatureRequests = []
      this.contactsList = []
      this.contactOwnerAccount = null
      this.contactRelationOwnerAccount = []
      this.showAllDocuments = false
      this.showAllRentalDocuments = false
      this.showCondominiumDocuments = false
      this.showAllSignatures = false
      this.selectedSignRequest = null
      this.selectedPopOverEvent = null
      this.tenancySignRequest = null
      this.showDigitalSignature = false
    },
    hitsToSuggestions(hits) {
      return [{ data: hits }]
    },
    onChange(refine, email) {
      if (email?.length > 0) {
        refine(email)
      }
    },
    rentalDocumentUpdated(document) {
      MediaApi.listDocuments([document.id]).then(response => {
        const newDoc = response.documents[0]
        this.rentalDocuments.forEach((doc, index) => {
          if (doc.id === document.id) {
            const el = this.listTenancies.find(x => x.documentIds.includes(document.id))
            if (el) {
              let name = ''
              const ids = [...new Set([el.tenant.billingContactId].concat(el.tenant.contactIds))]
              ids.forEach(element => {
                name += this.contacts.find(x => x.id === element)?.name + ', '
              })
              newDoc.label = el.numericId + ': ' + (name !== '' ? name.slice(0, -2) : '')
              newDoc.contractNumber = el.numericId
              newDoc.tenancyId = el.id
            }
            this.rentalDocuments.splice(index, 1, newDoc)
          }
        }, this)
        this.updateVisibleContactDocuments()
      }).catch(e => {
        console.log(e)
      })
    },
    onTenantChangeRentalDocumentsAdded({ entries }) {
      if (!entries?.length || !this.listTenancies?.length) {
        return
      }
      const contacts = this.contactsList.concat(this.relationsContacts)
      let updated = false
      entries.forEach(({ tenancyId, document }) => {
        if (!tenancyId || !document?.id) {
          return
        }
        const tenancy = this.listTenancies.find(item => item.id === tenancyId)
        if (!tenancy) {
          return
        }
        if (!tenancy.documentIds.includes(document.id)) {
          tenancy.documentIds.push(document.id)
          updated = true
        }
        if (this.rentalDocuments.some(item => item.id === document.id)) {
          return
        }
        const object = this.listbuilding.find(item => item.id === tenancy.objectId)
        const decoratedDocument = { ...document }
        let name = ''
        const ids = [...new Set([tenancy.tenant?.billingContactId].concat(tenancy.tenant?.contactIds || []))]
        ids.forEach(contactId => {
          name += contacts.find(contact => contact.id === contactId)?.name + ', '
        })
        if (object) {
          decoratedDocument.objectOwnerId = object.ownerId
        } else {
          decoratedDocument.objectOwnerId = ''
        }
        decoratedDocument.label = tenancy.numericId + ': ' + (name !== '' ? name.slice(0, -2) : '')
        decoratedDocument.contractNumber = tenancy.numericId
        decoratedDocument.tenancyId = tenancy.id
        this.rentalDocuments.unshift(decoratedDocument)
        updated = true
      })
      if (updated) {
        this.updateVisibleContactDocuments()
      }
    },
    loadRentalDocument(tenancies: ObjecttenancyTenancy[], contacts: (Contactcontactv2Company | V2Person)[], objects: ObjectobjectObject[], requestKey = this.contactLoadRequestKey) {
      if (requestKey !== this.contactLoadRequestKey) {
        return
      }
      this.listTenancies = [...tenancies]

      const documentIds = this.listTenancies.flatMap(tenancy => tenancy.documentIds)

      const uniqueDocumentIds = [...new Set(documentIds)]
      if (uniqueDocumentIds.length > 0) {
        MediaApi.listDocuments(uniqueDocumentIds)
          .then(response => {
            if (requestKey !== this.contactLoadRequestKey) {
              return
            }
            this.rentalDocuments = response.documents.reverse()
            for (let index = 0; index < this.rentalDocuments.length; index++) {
              const el = this.listTenancies.find(x => x.documentIds.includes(this.rentalDocuments[index].id))
              if (el) {
                const object = objects.find(x => x.id === el.objectId)
                let name = ''
                const ids = [...new Set([el.tenant.billingContactId].concat(el.tenant.contactIds))]
                ids.forEach(element => {
                  name += contacts.find(x => x.id === element)?.name + ', '
                })
                if (object) {
                  this.rentalDocuments[index].objectOwnerId = object.ownerId
                } else {
                  this.rentalDocuments[index].objectOwnerId = ''
                }
                this.rentalDocuments[index].label = el.numericId + ': ' + (name !== '' ? name.slice(0, -2) : '')
                this.rentalDocuments[index].contractNumber = el.numericId
                this.rentalDocuments[index].tenancyId = el.id
              }
            }
            this.updateVisibleContactDocuments()
          })
      } else {
        this.rentalDocuments = []
        this.updateVisibleContactDocuments()
      }
      this.loadSignatureRequest(requestKey)
    },
    condominiumDocumentUpdated(document) {
      MediaApi.listDocuments([document.id]).then(response => {
        const newDoc = response.documents[0]
        this.condominiumDocs.forEach((doc, index) => {
          if (doc.id === document.id) {
            const el = this.listCondominiums.find(x => x.documentIds.includes(document.id))
            if (el) {
              let name = ''
              const ids = [...new Set([el.owner.billingContactId].concat(el.owner.contactIds))]
              ids.forEach(element => {
                name += this.contacts.find(x => x.id === element)?.name + ', '
              })
              newDoc.condominiumId = el.id
              newDoc.label = el.numericId + ': ' + (name !== '' ? name.slice(0, -2) : '')
              newDoc.contractNumber = el.numericId
            }
            this.condominiumDocs.splice(index, 1, newDoc)
          }
        })
        this.updateVisibleContactDocuments()
      }).catch(e => {
        console.log(e)
      })
    },
    loadCondominiumDocuments(condominiums: CondominiumCondominium[], contacts: (Contactcontactv2Company | V2Person)[], objects: ObjectobjectObject[], requestKey = this.contactLoadRequestKey) {
      if (requestKey !== this.contactLoadRequestKey) {
        return
      }
      this.listCondominiums = [...condominiums]

      const documentIds = this.listCondominiums.flatMap(condominium => condominium.documentIds)

      const uniqueDocumentIds = [...new Set(documentIds)]
      if (uniqueDocumentIds.length > 0) {
        MediaApi.listDocuments(uniqueDocumentIds)
          .then(response => {
            if (requestKey !== this.contactLoadRequestKey) {
              return
            }
            this.condominiumDocs = response.documents.reverse()
            for (let index = 0; index < this.condominiumDocs.length; index++) {
              const el = this.listCondominiums.find(x => x.documentIds.includes(this.condominiumDocs[index].id))
              if (el) {
                const object = objects.find(x => x.id === el.objectId)
                let name = ''
                const ids = [...new Set([el.owner.billingContactId].concat(el.owner.contactIds))]
                ids.forEach(element => {
                  name += contacts.find(x => x.id === element)?.name + ', '
                })
                if (object) {
                  this.condominiumDocs[index].objectOwnerId = object.ownerId
                } else {
                  this.condominiumDocs[index].objectOwnerId = ''
                }
                this.condominiumDocs[index].condominiumId = el.id
                this.condominiumDocs[index].label = el.numericId + ': ' + (name !== '' ? name.slice(0, -2) : '')
                this.condominiumDocs[index].contractNumber = el.numericId
              }
            }
            this.updateVisibleContactDocuments()
          })
      } else {
        this.condominiumDocs = []
        this.updateVisibleContactDocuments()
      }
    },
    onSelect(selected) {
      if (selected) {
        const selectedContactId = selected.item.objectID
        this.redirectToDetailPage(selectedContactId)
        this.$nextTick(function () {
          this.emailSuggestion = ''
          this.refreshAutosuggestKey = Math.floor(Math.random() * 100)
        })
      }
    },
    forwardMail(email) {
      this.prepareForwardMail(email)
    },
    deletedMail(mailId) {
      if (mailId) {
        const index = this.mailList.findIndex(x => x.id === mailId)
        if (index > -1) {
          this.mailList.splice(index, 1)
        }
      }
    }
  }
})
</script>

<style lang="scss" scoped>

.link {
  left: 47%;
}

.card-data {
  p {
    padding: 0.7rem 0.7rem;
  }

  & > .row.m-0:nth-child(odd) {
    background-color: $color-bg-white;
  }

  & > .row.m-0:nth-child(even) {
    background-color: $color-bg-white;
  }

  a:hover {
    text-decoration: none;
  }
}

.pointer-link {
  cursor: pointer;
  text-decoration: underline;
  color: #333;
}

.auto-size {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.max-w-subject) {
  @media (min-width: 768px) {
    max-width: 15vw;
  }
}

:deep(.align-icon > div) {
  float: right;
}

:deep(.table-responsive) {
  margin-bottom: 0;
}

:deep(table.table) {
  border-radius: 0;
  box-shadow: none;

  tbody > .b-table-row-selected td {
    color: inherit !important;
  }

  tbody > tr:hover td {
    background-color: rgb(240 240 240) !important
  }

  @media (min-width: 768px) {
    tbody > tr td {
      background-color: $color-bg-white !important;
    }
  }

  .clickable-cell {
    padding: 0.4rem 0.75rem;
  }
}

:deep(thead > tr > th) {
  background-color: #f6f6f6bd !important;
}

.letter-img {
  width: 15px;
}

:deep(svg.ticket-icon path) {
  fill: #fff
}

:deep(.no-lower-height.lower-height) {
  margin-bottom: 0;

  & > .card-body {
    padding: 1.25rem !important;
  }
}
</style>
